import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useContext, useMemo } from 'react';
import { useEditingProps, useEditItem, useGetZ3kObject } from '../utils/unpack-props';
import { AnalogOutput, ModBusSignal } from 'z3k-config';
import { MaterialDropdown, MaterialPhysicalInputOutputSelection, RangeField, StrictNumberInput, } from 'console/shared/components/fields';
import { ErrorBoundary } from 'shared/components/error-boundary';
import { physical_and_modbus_analog_output_options } from 'z3k-parser/lib/z3k-selectors';
import { Z3KSettingsEditor } from 'console/device-settings/z3k/provider/types';
import { Control } from 'console/shared/components/fields/control';
import { TextInputWrapper } from 'console/shared/components/fields/text';
import { objects2ids } from 'z3k-parser/lib';
import { SettingContainer } from '../material-utils/settings-card/container';
const MULT_OPTIONS = [
    { text: 'Без множителя', value: 1 },
    { text: 'x10', value: 10 },
    { text: 'x100', value: 100 },
    { text: 'x1000', value: 1000 },
];
const units_options = AnalogOutput.unit_select.map((unit_spec) => ({
    value: unit_spec.value,
    text: `${unit_spec.value_title} (${unit_spec.unit_abb})`,
}));
const z3k_name = 'analog_outputs';
export const AnalogOutputComponent = memo(function AnalogOutputComponentImpl() {
    const item = useGetZ3kObject(z3k_name);
    const id = item.id;
    const onChange = useEditItem(id, z3k_name);
    const { config } = useContext(Z3KSettingsEditor);
    const nameEditingProps = useEditingProps(id, z3k_name, 'name');
    const physicalOutputEditingProps = useEditingProps(id, z3k_name, 'physical_output');
    const minValueEditingProps = useEditingProps(id, z3k_name, 'min_value');
    const maxValueEditingProps = useEditingProps(id, z3k_name, 'max_value');
    const stepEditingProps = useEditingProps(id, z3k_name, 'step');
    const unitsEditingProps = useEditingProps(id, z3k_name, 'unit');
    const multEditingProps = useEditingProps(id, z3k_name, 'mult');
    const modbus_signal_ids = useMemo(() => objects2ids(ModBusSignal.get.all(config)), [config]);
    const is_modbus_output = useMemo(() => AnalogOutput.isModbusOutput(item, modbus_signal_ids), [item, modbus_signal_ids]);
    const onChangePhysicalOutput = useCallback((physical_output_id) => {
        //Если это встроенный аналоговый выход
        if (physical_output_id != null &&
            !AnalogOutput.isModbusOutput({ physical_output: physical_output_id }, modbus_signal_ids)) {
            onChange({
                ...item,
                physical_output: physical_output_id,
                unit: AnalogOutput.Unit.Voltage,
                mult: 10,
                step: 1,
                min_value: 0,
                max_value: 100,
            });
        }
        else {
            onChange({
                ...item,
                physical_output: physical_output_id != null ? physical_output_id : -1,
            });
        }
    }, [onChange, item, modbus_signal_ids]);
    const units_field = useCallback((input_props) => (_jsx(MaterialDropdown, { ...input_props, disabled: !is_modbus_output, options: units_options })), [units_options, is_modbus_output]);
    const mult_field = useCallback((input_props) => (_jsx(MaterialDropdown, { ...input_props, options: MULT_OPTIONS })), [MULT_OPTIONS]);
    const step_field = useCallback((input_props) => (_jsx(StrictNumberInput, { editDividedBy: item.mult ?? 1, min: is_modbus_output ? 1 : 0.1, max: is_modbus_output ? 10_000 : 10, ...input_props })), [item.mult, is_modbus_output]);
    const _physical_and_modbus_analog_output_options = physical_and_modbus_analog_output_options(config);
    return (_jsx(ErrorBoundary, { children: _jsxs(SettingContainer, { children: [_jsx(Control, { label: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", field: TextInputWrapper, help_id: "analog_output_name", ...nameEditingProps }), _jsx(MaterialPhysicalInputOutputSelection, { label: "\u0423\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u043E \u0432\u044B\u0432\u043E\u0434\u0430", options: _physical_and_modbus_analog_output_options, help_id: "analog_output_physical_output", ...physicalOutputEditingProps, onChange: onChangePhysicalOutput }), _jsx(RangeField, { label: 'Значения на выходе, ' + AnalogOutput.getUnitAbb(item), help_id: "analog_output_range", min_field: minValueEditingProps, minCaption: "\u041C\u0438\u043D", max_field: maxValueEditingProps, maxCaption: "\u041C\u0430\u043A\u0441", maxCaptionEnd: true, editDividedBy: item.mult ?? 1, min: is_modbus_output ? -1_000_000 : 0, max: is_modbus_output ? 1_000_000 : 10 }), _jsx(Control, { label: "\u0428\u0430\u0433 \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u044F", help_id: "analog_output_step", ...stepEditingProps, field: step_field }), _jsx(Control, { label: "\u0415\u0434\u0438\u043D\u0438\u0446\u044B \u0438\u0437\u043C\u0435\u0440\u0435\u043D\u0438\u044F", ...unitsEditingProps, field: units_field, help_id: "analog_output_unit" }), is_modbus_output && (_jsx(Control, { label: "\u041A\u043E\u044D\u0444\u0444\u0438\u0446\u0438\u0435\u043D\u0442 \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u044F", ...multEditingProps, field: mult_field, help_id: "analog_output_mult" }))] }) }));
});
